import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';
import { useToasts } from 'react-toast-notifications';
import { useSelector } from 'react-redux';
import { Reducers } from 'store/types';
import { DEFAULT_PAGINATION_ITEMS_PER_PAGE, MAX_PAGINATION_ITEMS_PER_PAGE, LOGGER_LOG_TYPE } from 'Config';
import { UserProfile } from 'api/account/models/UserProfile';
import UsersService from 'api/users/UsersService';
import GeofencesService from 'api/geofences/GeofencesService';
import { GeofencesListCountersDto, GeofencesListItemDto } from 'api/geofences/models/GeofencesListItemDto';
import { GeofencesSearchCriteria } from 'api/geofences/models/GeofencesSearchCriteria';
import Logger from 'common/services/Logger';
import Loading from 'common/services/Loading';
import ScreenTitle from 'common/components/screenTitle/ScreenTitle';
import ScreenHeaderButton from 'common/components/screenHeader/ScreenHeaderButton';
import MoneyFormat from 'common/components/moneyFormat/MoneyFormat';
import QuestionYesNo from 'common/components/questionYesNo/QuestionYesNo';
import Dropdown from 'common/components/dropdown/Dropdown';
import DropdownItem from 'common/components/dropdown/DropdownItem';
import DataTableWithSidebar from 'common/components/dataTable/DataTableWithSidebar';
import { DataTableColumn, OrderData } from 'common/components/dataTable/models';
import InfoCard from 'common/components/infoCard/InfoCard';
import useBidirectionalScroll from 'common/hooks/useBidirectionalScroll';
import colors from 'styles/export/colors.module.scss';
import AddIcon from 'assets/svg/desktop_add_new.svg';
import CoinsIcon from 'assets/svg/coins.svg';
import HourglassIcon from 'assets/svg/hour-glass_white.svg';
import PinIcon from 'assets/svg/desktop_GPS_white.svg';
import { CompactPageLayout, compactPageLayoutStyles } from 'common/components/compactPageLayout';
import InputSearch from 'common/components/inputSearch/InputSearch';
import { useDebouncedCallback } from 'use-debounce';
import { FaEllipsisH } from 'react-icons/fa';
import ExportListingButton from 'common/components/exportListingButton/ExportListingButton';
import VatNotice from 'common/components/vatNotice/VatNotice';
import styles from './GeofencesListScreen.module.scss';

const FILTERS_CACHE_KEY = 'GEOFENCESLISTFILTER';

const ORDER_COLUMN_TO_FIELD: Record<string, keyof GeofencesListItemDto> = {
    'name': 'name',
    'total_costs': 'totalCosts',
    'total_duration_minutes': 'totalDuration',
};

const INFO_CARD_COLORS = [
    colors.counterTeal1,
    colors.counterTeal2,
    colors.counterBlue1,
];

interface Filters {
    page?: number;
    allIn?: string;
    orderColumn?: string;
    orderBy?: string;
    scrollToItemId?: string;
}

const GeofencesListScreen = () => {
    const { t } = useTranslation();
    const history = useHistory();
    const { addToast } = useToasts();
    const userProfile = useSelector<Reducers, UserProfile | null>(state => state.authentication.profile);
    const hasUsersWritePolicy = UsersService.hasPolicies(userProfile?.policies || [], ['SETTINGUP_COST_CENTER_WRITE']);

    const [itemToRemove, setItemToRemove] = useState<GeofencesListItemDto | null>(null);
    const [showRemoveModal, setShowRemoveModal] = useState<boolean>(false);
    const [geofences, setGeofences] = useState<GeofencesListItemDto[]>([]);
    const [totalItems, setTotalItems] = useState<number>(0);
    const [isLoadingMore, setIsLoadingMore] = useState(false);
    const [counters, setCounters] = useState<GeofencesListCountersDto>();

    const isFirstLoad = useRef(true);
    const countersLoaded = useRef(false);
    const exportListingRef = useRef<{ exportExcel: () => Promise<void> } | null>(null);
    const previousCriteria = useRef<GeofencesSearchCriteria | null>(null);
    const isLoadingData = useRef(false);

    const [criteria, setCriteria] = useState<GeofencesSearchCriteria>({
        itemsPerPage: DEFAULT_PAGINATION_ITEMS_PER_PAGE,
        page: 1,
        orderColumn: 'name',
        orderBy: 'asc',
    });

    // Bidirectional scroll hook
    const bidirectionalScroll = useBidirectionalScroll({
        cacheKey: FILTERS_CACHE_KEY,
        itemsPerPage: DEFAULT_PAGINATION_ITEMS_PER_PAGE,
    });

    const {
        loadedPageRange,
        isLoadingPrevious,
        scrollToRowId: scrollToItemId,
        shouldLoadAdjacentPages,
        setLoadedPageRange,
        setIsLoadingPrevious,
        resetScrollState,
        calculatePageFromIndex,
        prepareLoadPrevious,
        adjustScrollAfterPrepend,
        finishRestoration,
        initiateRestoration,
        getHasMore,
        getHasMorePrevious,
        getOnLoadMore,
        getOnLoadPrevious,
    } = bidirectionalScroll;

    // Order state for DataTable
    const [order, setOrder] = useState<OrderData<GeofencesListItemDto>>({
        field: 'name',
        orderColumn: 'name',
        isOrderAsc: true,
    });

    const getData = async (isLoadMore = false, shouldUpdateCounters = false) => {
        if (isLoadingData.current) return;
        isLoadingData.current = true;

        try {
            if (!isLoadMore) {
                Loading.show();
            } else {
                setIsLoadingMore(true);
            }

            saveOrdernationFilters();

            let countersDB = null;
            if (shouldUpdateCounters) {
                countersDB = await GeofencesService.getCountersList(criteria);
            }

            const geofenceItems = await GeofencesService.getList(criteria);
            setTotalItems(geofenceItems.totalItems);

            if (isLoadMore) {
                setGeofences(prev => [...prev, ...geofenceItems.items]);
                setLoadedPageRange(prev => ({ ...prev, max: criteria.page }));
            } else {
                setGeofences(geofenceItems.items ?? []);
            }

            if (countersDB) {
                setCounters(countersDB);
                countersLoaded.current = true;
            }

            // Handle scroll restoration
            if (shouldLoadAdjacentPages.current) {
                shouldLoadAdjacentPages.current = false;
                const currentPage = criteria.page;
                const totalPages = Math.ceil(geofenceItems.totalItems / (criteria.itemsPerPage || DEFAULT_PAGINATION_ITEMS_PER_PAGE));

                if (currentPage > 1) {
                    try {
                        const prevCriteria = { ...criteria, page: currentPage - 1 };
                        const prevResult = await GeofencesService.getList(prevCriteria);
                        setGeofences(prev => [...prevResult.items, ...prev]);
                        setLoadedPageRange(prev => ({ ...prev, min: currentPage - 1 }));
                    } catch (e) {
                        Logger.error(LOGGER_LOG_TYPE.ALARMS, `Couldn't load previous page`, e);
                    }
                }

                if (currentPage < totalPages) {
                    try {
                        const nextCriteria = { ...criteria, page: currentPage + 1 };
                        const nextResult = await GeofencesService.getList(nextCriteria);
                        setGeofences(prev => [...prev, ...nextResult.items]);
                        setLoadedPageRange(prev => ({ ...prev, max: currentPage + 1 }));
                    } catch (e) {
                        Logger.error(LOGGER_LOG_TYPE.ALARMS, `Couldn't load next page`, e);
                    }
                }

                finishRestoration();
            }
        } catch (error) {
            Logger.error(LOGGER_LOG_TYPE.ALARMS, `Couldn't get geofence list`, error);
            addToast(t('common.messages.error_load_info'), { appearance: 'error' });
            resetScrollState();
        } finally {
            Loading.hide();
            setIsLoadingMore(false);
            isLoadingData.current = false;
        }
    };

    // Handler for infinite scroll load more
    const loadMore = useCallback(() => {
        if (isLoadingData.current || isLoadingMore || geofences.length >= totalItems) return;
        const nextPage = loadedPageRange.max + 1;
        setCriteria(prev => ({ ...prev, page: nextPage }));
    }, [isLoadingMore, geofences.length, totalItems, loadedPageRange.max]);

    // Load previous pages (bidirectional scroll)
    const loadPrevious = useCallback(async () => {
        if (isLoadingPrevious || loadedPageRange.min <= 1) return;

        setIsLoadingPrevious(true);
        const prevPage = loadedPageRange.min - 1;

        try {
            prepareLoadPrevious();
            const prevCriteria: GeofencesSearchCriteria = {
                ...criteria,
                page: prevPage,
                itemsPerPage: DEFAULT_PAGINATION_ITEMS_PER_PAGE,
            };
            const result = await GeofencesService.getList(prevCriteria);

            setGeofences(prev => [...result.items, ...prev]);
            setLoadedPageRange(prev => ({ ...prev, min: prevPage }));
        } catch (error) {
            Logger.error(LOGGER_LOG_TYPE.ALARMS, `Couldn't get previous geofences page`, error);
            addToast(t('common.messages.error_load_info'), { appearance: 'error' });
        } finally {
            setIsLoadingPrevious(false);
        }
    }, [isLoadingPrevious, loadedPageRange.min, criteria, addToast, t]);

    // Adjust scroll position after prepending items
    useEffect(() => {
        if (!isLoadingPrevious) {
            adjustScrollAfterPrepend();
        }
    }, [geofences, isLoadingPrevious, adjustScrollAfterPrepend]);

    // Handler de ordenação
    const handleOrderChange = (orderData: OrderData<GeofencesListItemDto>) => {
        setOrder(orderData);
        setGeofences([]);
        resetScrollState();
        setTotalItems(0);
        setCriteria(prev => ({
            ...prev,
            page: 1,
            orderBy: orderData.isOrderAsc ? 'asc' : 'desc',
            orderColumn: orderData.orderColumn || orderData.field as string,
        }));
    };

    const navigateTo = (type?: string, id?: string, newTab?: boolean) => {
        const url = id ? `/geofences/${type}/${id}` : `/geofences/${type}`;

        if (newTab) {
            window.open(url, '_blank');
        } else {
            history.push(url);
        }
    };

    const onRowClick = (row: GeofencesListItemDto, index: number, event: any) => {
        if (row.id) {
            event.preventDefault();
            event.stopPropagation();

            const actualPage = calculatePageFromIndex(index);

            const newFilters: Filters = {
                allIn: criteria.allIn,
                orderBy: criteria.orderBy,
                orderColumn: criteria.orderColumn,
                page: actualPage,
                scrollToItemId: row.id,
            };
            saveFiltersOnCache(newFilters);

            if (event.ctrlKey) {
                window.open(`/geofences/details/${row.id}`, '_blank');
                return;
            }
            navigateTo('details', row.id);
        }
    };

    const saveOrdernationFilters = () => {
        saveFiltersOnCache({ allIn: criteria.allIn, orderBy: criteria.orderBy, orderColumn: criteria.orderColumn });
    };

    const saveFiltersOnCache = (f: Filters) => {
        if (f.scrollToItemId && f.page) {
            localStorage.setItem(FILTERS_CACHE_KEY, JSON.stringify(f));
        } else {
            const { page: _page, scrollToItemId: _scrollToItemId, ...filtersWithoutScrollData } = f;
            localStorage.setItem(FILTERS_CACHE_KEY, JSON.stringify(filtersWithoutScrollData));
        }
    };

    useEffect(() => {
        if (isFirstLoad.current === true) {
            loadFilters();
        } else {
            const isLoadMore = criteria.page > loadedPageRange.max && geofences.length > 0;

            const prev = previousCriteria.current;
            const onlyOrderOrPageChanged = prev &&
                prev.allIn === criteria.allIn;

            const shouldLoadCounters = !countersLoaded.current || !onlyOrderOrPageChanged;
            getData(isLoadMore, shouldLoadCounters);
            previousCriteria.current = { ...criteria };
        }
    }, [criteria]);

    const loadFilters = () => {
        isFirstLoad.current = false;

        const cachedFilters = localStorage.getItem(FILTERS_CACHE_KEY);
        if (cachedFilters != null) {
            const f = JSON.parse(cachedFilters) as Filters;

            if (f.scrollToItemId) {
                const startPage = initiateRestoration({
                    page: f.page || 1,
                    scrollToItemId: f.scrollToItemId,
                });

                const cleanedFilters = { ...f, scrollToItemId: undefined, page: 1 };
                localStorage.setItem(FILTERS_CACHE_KEY, JSON.stringify(cleanedFilters));

                setCriteria(prev => ({
                    ...prev,
                    allIn: f.allIn,
                    orderBy: f.orderBy,
                    orderColumn: f.orderColumn,
                    page: startPage,
                    itemsPerPage: DEFAULT_PAGINATION_ITEMS_PER_PAGE,
                }));

                if (f.orderBy && f.orderColumn) {
                    setOrder({
                        field: ORDER_COLUMN_TO_FIELD[f.orderColumn] || f.orderColumn as keyof GeofencesListItemDto,
                        orderColumn: f.orderColumn,
                        isOrderAsc: f.orderBy === 'asc',
                    });
                }
                return;
            }

            resetScrollState();
            localStorage.setItem(FILTERS_CACHE_KEY, JSON.stringify({ ...f, scrollToItemId: undefined, page: 1 }));

            setCriteria(prev => ({
                ...prev,
                allIn: f.allIn,
                orderBy: f.orderBy,
                orderColumn: f.orderColumn,
                page: 1,
            }));

            if (f.orderBy && f.orderColumn) {
                setOrder({
                    field: ORDER_COLUMN_TO_FIELD[f.orderColumn] || f.orderColumn as keyof GeofencesListItemDto,
                    orderColumn: f.orderColumn,
                    isOrderAsc: f.orderBy === 'asc',
                });
            }
        } else {
            setCriteria(prev => ({ ...prev, page: 1 }));
        }
    };

    const showRemoveItemDialog = (item: GeofencesListItemDto) => {
        setItemToRemove(item);
        setShowRemoveModal(true);
    };

    const onCancelRemove = () => {
        setItemToRemove(null);
        setShowRemoveModal(false);
    };

    const onRemove = async () => {
        if (itemToRemove === null) {
            addToast(t('common.messages.record_delete_error'), { appearance: 'error' });
            return;
        }

        try {
            await GeofencesService.remove(itemToRemove as any);
            setGeofences([]);
            resetScrollState();
            setTotalItems(0);
            countersLoaded.current = false;
            setCriteria(prev => ({ ...prev, page: 1 }));
            onCancelRemove();
            addToast(t('common.messages.record_delete_success'), {
                appearance: 'success',
            });
        }
        catch (error) {
            Logger.error(LOGGER_LOG_TYPE.REQUEST, `Couldn't delete geofence`, error);
            addToast(t('common.messages.record_delete_error'), { appearance: 'error' });
        }
    };

    const tableColumns: DataTableColumn<GeofencesListItemDto>[] = useMemo(() => [
        {
            field: 'name' as keyof GeofencesListItemDto,
            name: t('geofences.list.name') as string,
            orderColumn: 'name',
            width: 250,
            renderCell: row => <>{row.name}</>,
        },
        {
            field: 'totalCosts' as keyof GeofencesListItemDto,
            name: t('geofences.list.charges') as string,
            orderColumn: 'total_costs',
            width: 200,
            headerNote: `${t('common.unit')}: ${t('common.euro')}`,
            renderCell: row => <div className={styles.costColumn}>
                <MoneyFormat fixedDecimalScale={true} value={row.totalCosts} />
            </div>,
        },
        {
            field: 'totalDuration' as keyof GeofencesListItemDto,
            name: t('common.duration') as string,
            orderColumn: 'total_duration_minutes',
            width: 200,
            headerNote: `${t('common.unit')}: h`,
            renderCell: row => <div className={styles.costColumn}>
                {row.totalDuration} h
            </div>,
        },
    ], [t]);

    const renderTableActionCell = (row: GeofencesListItemDto) => {
        const urlDetails = `geofences/details/${row.id}`;
        const urlEdit = `geofences/edit/${row.id}`;
        return (
            <Dropdown
                options={
                    <>
                        <DropdownItem url={urlDetails}>
                            {t('common.details')}
                        </DropdownItem>
                        {hasUsersWritePolicy && <DropdownItem url={urlEdit} disabled={row.hasCharges ?? false} disabled_message={t('geofences.cant_update_has_charges')}>
                            {t('common.edit')}
                        </DropdownItem>}
                        {hasUsersWritePolicy && <DropdownItem onClick={() => showRemoveItemDialog(row)} disabled={row.hasCharges ?? false} disabled_message={t('geofences.cant_delete_has_charges')}>
                            {t('common.remove')}
                        </DropdownItem>}
                    </>
                }
            >
                <div>
                    <FaEllipsisH />
                </div>
            </Dropdown>
        );
    };

    const debounced = useDebouncedCallback((value: string) => {
        if (value === criteria.allIn) return;
        countersLoaded.current = false;
        setGeofences([]);
        resetScrollState();
        setTotalItems(0);
        setCriteria(prev => ({ ...prev, page: 1, allIn: value }));
    }, 500);

    const renderCounterBoxes = () => {
        let hours = 0;
        let minutes = 0;

        if (counters) {
            const hoursHelper = counters.totalDuration / 60;
            hours = Math.floor(hoursHelper);
            minutes = hours > 0 ? (Math.fround((hoursHelper - hours) * 60)) : counters.totalDuration;
        }

        return (
            <div className={compactPageLayoutStyles.rowCounters}>
                <InfoCard
                    label={t('geofences.list.num_geofences') as string}
                    value={counters?.numGeofences ?? 0}
                    icon={PinIcon}
                    backgroundColor={INFO_CARD_COLORS[0]}
                />
                <InfoCard
                    label={t('geofences.list.total_charges') as string}
                    value={counters?.totalCharges ?? 0}
                    icon={CoinsIcon}
                    backgroundColor={INFO_CARD_COLORS[1]}
                    isMoney
                />
                <InfoCard
                    label={t('geofences.list.total_duration') as string}
                    value={`${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')} h`}
                    icon={HourglassIcon}
                    backgroundColor={INFO_CARD_COLORS[2]}
                />
            </div>
        );
    };

    const fetchAllForExport = useCallback(async () => {
        const result = await GeofencesService.getList({
            ...criteria,
            page: 1,
            itemsPerPage: MAX_PAGINATION_ITEMS_PER_PAGE,
        });
        return result.items;
    }, [criteria]);

    return (
        <ScreenTitle title={t('geofences.list.title')}>
            <CompactPageLayout
                title={t('geofences.list.title') as string}
                contentAreaClassName={styles.contentArea}
                middleContent={
                    <div className={styles.searchContainer}>
                        <InputSearch
                            onChangeValue={debounced}
                            placeholder={t('geofences.list.search_by_name')}
                            defaultValue={criteria.allIn}
                        />
                    </div>
                }
                headerButtons={
                    <>
                        <ExportListingButton exportRef={exportListingRef} />
                        {hasUsersWritePolicy && <ScreenHeaderButton icon={AddIcon} onClick={() => navigateTo('new')} />}
                    </>
                }
            >
                {counters && (
                    <div className={compactPageLayoutStyles.countersSection}>
                        <VatNotice />
                        {renderCounterBoxes()}
                    </div>
                )}

                <div className={compactPageLayoutStyles.listTblWrapper}>
                    {isFirstLoad.current === false && (
                        <DataTableWithSidebar
                            cacheKey="GEOFENCES_TABLE"
                            columns={tableColumns}
                            rows={geofences}
                            defaultOrder={order}
                            onOrderChange={handleOrderChange}
                            onRowClick={(row, index, event) => onRowClick(row, index, event)}
                            onHref={row => `/geofences/details/${row.id}`}
                            onRenderAction={renderTableActionCell}
                            onRowId={row => row.id ?? ''}
                            scrollToRowId={scrollToItemId}
                            infiniteScroll={{
                                isLoading: isLoadingMore,
                                hasMore: getHasMore(geofences.length, totalItems),
                                onLoadMore: getOnLoadMore(loadMore),
                                hasMorePrevious: getHasMorePrevious(),
                                isLoadingPrevious: isLoadingPrevious,
                                onLoadPrevious: getOnLoadPrevious(loadPrevious),
                            }}
                            exportRef={exportListingRef}
                            hideExportInSidebar={true}
                            exportExcel={{
                                fetchAll: fetchAllForExport,
                                fileName: t('geofences.title') as string,
                            }}
                        />
                    )}
                </div>
            </CompactPageLayout>
            <QuestionYesNo
                onNo={onCancelRemove}
                onYes={onRemove}
                isVisible={showRemoveModal}
                message={t('common.messages.remove_record_with_ident', { name: itemToRemove?.name ?? '' })} />
        </ScreenTitle>
    );
};

export default GeofencesListScreen;
